/**
 * SchoolChangePasswordPage — forced password change after login.
 *
 * Reached when the backend returns `must_change_password: true` in the login
 * response (first login or after any reset). User cannot access their portal
 * until they set a new password.
 *
 * URL: /school/:tenantId/change-password
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '@/lib/store';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SchoolChangePasswordPage = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { user, userType, logout } = useAuthStore();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next || !confirm) {
      toast.error('Please fill in all fields'); return;
    }
    if (next.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (next !== confirm) { toast.error('New passwords do not match'); return; }
    if (next === current) { toast.error('New password must be different from current'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/school/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to change password');

      // Update local user so the enforce guard stops firing
      const store = useAuthStore.getState();
      if (store.user) {
        store.setAuth(
          store.token,
          { ...store.user, must_change_password: false },
          store.userType,
          store.eventId,
          store.permissions,
        );
      }

      toast.success('Password changed — welcome!');
      if (userType === 'admin') navigate(`/admin/schools/${tenantId}`);
      else if (userType === 'teacher') navigate(`/school/${tenantId}/teacher`);
      else if (userType === 'student') navigate(`/school/${tenantId}/student`);
      else navigate(`/school/${tenantId}/parent`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    logout();
    navigate(`/school/${tenantId}/login`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50 px-4">
      <Card className="w-full max-w-md shadow-xl" data-testid="school-change-password-card">
        <CardContent className="p-8">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="h-14 w-14 rounded-full bg-indigo-100 flex items-center justify-center mb-3">
              <KeyRound className="h-7 w-7 text-indigo-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Set a new password</h1>
            <p className="text-sm text-slate-500 mt-1">
              {user?.name ? `Hi ${user.name}, ` : ''}for security, you must change your password before continuing.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="current">Current password</Label>
              <Input
                id="current"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                data-testid="school-change-pw-current"
              />
            </div>
            <div>
              <Label htmlFor="next">New password</Label>
              <Input
                id="next"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                data-testid="school-change-pw-new"
              />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="school-change-pw-confirm"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full"
              data-testid="school-change-pw-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <span className="inline-flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" /> Update & continue
                </span>
              )}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={handleSignOut}
              className="text-sm text-slate-500 hover:text-slate-700 underline"
              data-testid="school-change-pw-signout"
            >
              Sign out instead
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SchoolChangePasswordPage;
